import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { GridApi, IRowNode } from 'ag-grid-community';
import { useLocation, useNavigate } from 'react-router-dom';

import PeriodsContext from 'contexts/PeriodsContext';
import AnalyticsContext from 'contexts/AnalyticsContext';
import ToastContext from 'contexts/ToastContext';
import SelectedEntityContext, {
  EntityChangeGuard,
} from '../contexts/SelectedEntityContext';
import PerformanceBoardSlideOutContext from '../contexts/PerformanceBoardSlideOutContext';
import useEntityField from '../../TargetManager/hooks/useEntityField';
import useQueryParams from 'hooks/useQueryParams';

export const ENTITY_VALUE_QUERY_PARAM = 'ev';
export const PERIOD_QUERY_PARAM = 'p';

const useInitialEntityValue = () => {
  const urlParams = useQueryParams();

  const queriedEntityValue = urlParams.has(ENTITY_VALUE_QUERY_PARAM)
    ? (urlParams.get(ENTITY_VALUE_QUERY_PARAM) as string)
    : undefined;

  return queriedEntityValue;
};

const SelectedEntityProvider = ({
  board,
  gridApi,
  children,
}: {
  board: PerformanceBoardTypes.Board;
  gridApi: GridApi | undefined;
  children: JSX.Element | JSX.Element[];
}) => {
  const initialEntityValue = useInitialEntityValue();
  const { setIsOpen, isOpen } = useContext(PerformanceBoardSlideOutContext);
  const { selectedPeriod } = useContext(PeriodsContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { showToast } = useContext(ToastContext);
  const entityField = useEntityField(board.dataType);
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedEntity, setSelectedEntity] = useState<string | undefined>();
  const [selectedEntityDocument, setSelectedEntityDocument] = useState<
    ElasticDocument | undefined
  >();

  // Kept in a ref so the current selection is readable inside onEntitySelected
  // without re-creating the callback on every selection change.
  const selectedEntityRef = useRef(selectedEntity);
  selectedEntityRef.current = selectedEntity;

  // Refs so onEntityUnavailable can read the current period/URL without changing
  // identity (it's threaded down to async data callbacks).
  const selectedPeriodRef = useRef(selectedPeriod);
  selectedPeriodRef.current = selectedPeriod;
  const locationRef = useRef(location);
  locationRef.current = location;

  // A guard registered by the slideout when it holds unsaved work (e.g. an
  // in-progress bonus override). When set, switching to a different entity
  // routes through it so the user can confirm before their changes are lost.
  const entityChangeGuardRef = useRef<EntityChangeGuard | undefined>(undefined);
  const setEntityChangeGuard = useCallback(
    (guard: EntityChangeGuard | undefined) => {
      entityChangeGuardRef.current = guard;
    },
    [],
  );

  // The selected entity has no data for the current period — close the slideout,
  // drop the ?ev= deep-link (so it doesn't re-open against the stale driver) and
  // tell the user why. Stable identity via refs so it can be handed to async
  // data callbacks (e.g. a bonus reward request that 400s).
  const onEntityUnavailable = useCallback(() => {
    const entity = selectedEntityRef.current;
    if (entity) {
      const periodLabel = selectedPeriodRef.current?.label;
      showToast(
        periodLabel
          ? `${entity} is not available for ${periodLabel}`
          : `${entity} is not available for this period`,
      );
    }
    setIsOpen(false);
    const params = new URLSearchParams(locationRef.current.search);
    if (params.has(ENTITY_VALUE_QUERY_PARAM)) {
      params.delete(ENTITY_VALUE_QUERY_PARAM);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [showToast, setIsOpen, navigate]);

  const onEntitySelected = useCallback(
    (newValue: string, skipUrlUpdate?: boolean) => {
      const applySelection = () => {
        setSelectedEntity(newValue);
        if (gridApi && !gridApi.isDestroyed() && entityField) {
          let document: ElasticDocument | undefined;
          gridApi.forEachNode((node) => {
            if (node.data && node.data[entityField] === newValue) {
              document = node.data;
            }
          });
          setSelectedEntityDocument(document);
        }
        setIsOpen(true);
        if (!skipUrlUpdate) {
          const existingParams = new URLSearchParams(location.search);
          existingParams.set(ENTITY_VALUE_QUERY_PARAM, newValue);
          navigate({ search: existingParams.toString() });
        }

        trackEvent('Board - Slide out opened', {
          boardName: board.name,
          boardId: board.id,
          boardDataType: board.dataType,
        });
      };

      // A registered guard only intercepts a genuine switch to a different
      // entity (not the URL-driven initial selection, nor re-clicking the same
      // row). It confirms with the user, then runs applySelection on confirm.
      if (
        !skipUrlUpdate &&
        entityChangeGuardRef.current &&
        newValue !== selectedEntityRef.current
      ) {
        entityChangeGuardRef.current(applySelection);
        return;
      }

      applySelection();
    },
    [
      entityField,
      gridApi,
      setIsOpen,
      trackEvent,
      board,
      navigate,
      location.search,
    ],
  );

  // Scroll the selected driver into view once the grid finishes rendering its
  // rows. This fires on `firstDataRendered`, so it covers a board refresh (the
  // remount reloads from the top and re-opens the slideout to the selected
  // driver via the `ev` URL param) and a deep-linked `?ev=` open — but not an
  // ordinary row click (no remount), so a visible row is never re-centred.
  // A fresh grid fires a new onGridReady → new gridApi, so this re-registers
  // per refresh. Boards over one cache block (1000 rows) whose target sits in
  // an unfetched block simply don't scroll (forEachNode won't find the node).
  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) {
      return;
    }

    const scrollToSelected = () => {
      const value = selectedEntityRef.current;
      if (!value || !entityField || gridApi.isDestroyed()) {
        return;
      }
      let targetNode: IRowNode | undefined;
      gridApi.forEachNode((node) => {
        if (node.data && node.data[entityField] === value) {
          targetNode = node;
        }
      });
      if (targetNode) {
        gridApi.ensureNodeVisible(targetNode, 'middle');
      }
    };

    gridApi.addEventListener('firstDataRendered', scrollToSelected);
    return () => {
      if (!gridApi.isDestroyed()) {
        gridApi.removeEventListener('firstDataRendered', scrollToSelected);
      }
    };
  }, [gridApi, entityField]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    setSelectedEntity(undefined);
    setSelectedEntityDocument(undefined);
  }, [isOpen]);

  useEffect(() => {
    if (
      initialEntityValue &&
      selectedPeriod &&
      initialEntityValue !== selectedEntity
    ) {
      onEntitySelected(initialEntityValue, true);
    }
  }, [initialEntityValue, onEntitySelected, selectedEntity, selectedPeriod]);
  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) {
      return;
    }
    gridApi.setGridOption('context', {
      selectedEntity,
      entityField,
    });
    gridApi.redrawRows();
  }, [gridApi, entityField, selectedEntity]);

  if (!entityField) {
    return null;
  }

  return (
    <SelectedEntityContext.Provider
      value={{
        entityField,
        selectedEntity,
        selectedEntityDocument,
        onEntitySelected,
        setEntityChangeGuard,
        onEntityUnavailable,
      }}
    >
      {children}
    </SelectedEntityContext.Provider>
  );
};

export default SelectedEntityProvider;
